import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';
import { CountUpNumber } from '@/components/atoms/CountUpNumber';
import { DiscoverCountSkeleton } from '@/components/atoms/DiscoverCountSkeleton';

import { formatCount } from '@/lib/discover/formatCount';

import { DiscoverHeroStripBackdrop } from './DiscoverHeroStripBackdrop';

/**
 * Facet/meta props common to every headline shape. The headline source
 * (`title` / `cityName`) is contributed by {@link DiscoverHeroStripHeadline}.
 */
interface DiscoverHeroStripBaseProps {
  /**
   * Headline count: in-range total (generic) or taste-matched total
   * (personalized). `null` = unresolved — skeleton, never a painted `0`
   * that later becomes non-zero (WEB-786).
   */
  total: number | null;
  /**
   * Distinct neighborhoods for the generic meta line. `null` = unresolved.
   */
  neighborhoods: number | null;
  /** Active time window label, e.g. "This week" or "Tonight". Rendered verbatim. */
  window: string;
  /**
   * FRE-917 — personalized viewer name. When supplied (authenticated viewer
   * with an honestly-derivable name), the headline renders
   * `Discover, {viewerName}.` instead of the `cityName` grammar. This controls
   * ONLY the heading grammar — the taste meta line is gated independently on
   * `personalizedMeta` (see below), so a personalized response with a
   * blank/missing name still renders the taste framing rather than dropping to
   * the generic neighborhoods meta. Omitted for anonymous / soaking viewers,
   * where the city grammar shows.
   */
  viewerName?: string;
  /**
   * FRE-917 — personalized taste meta counts. Presence is the personalization
   * signal for the meta line: when supplied (authenticated viewer with a real
   * taste signal), the meta switches to `{total} matches ·
   * {followedArtistCount} from artists you follow ·
   * {followedVenueCount} at venues you follow` — independent of whether
   * `viewerName` resolved. The "FROM ARTISTS" / "AT VENUES" fragments drop
   * individually when their count is `0`, so the line never claims a follow the
   * viewer doesn't have. Omitted for anonymous / soaking viewers, where the
   * generic events/neighborhoods meta shows.
   */
  personalizedMeta?: {
    readonly followedArtistCount: number;
    readonly followedVenueCount: number;
  };
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Headline source. Exactly one of the two paths must be supplied so the `<h1>`
 * can never render empty:
 *
 * - `cityName` — DS 1.5.0 Discover grammar. The headline renders as
 *   `Discover, <em>{cityName}</em>.` — the literal lead and trailing period are
 *   owned by the component, with the city name in the `--accent` role (DS
 *   `disc-top__title`; WEB-650 / DPL-40). Takes precedence over `title`.
 *   (@since FRE-898)
 * - `title` — legacy free-form path. The first whitespace-delimited word is
 *   rendered in `--accent`.
 *
 * The union requires at least one; omitting both is a compile error. (A blank
 * string still type-checks, so the component also guards at runtime.)
 */
type DiscoverHeroStripHeadline =
  { cityName: string; title?: string } | { cityName?: string; title: string };

export type DiscoverHeroStripProps = DiscoverHeroStripBaseProps & DiscoverHeroStripHeadline;

interface SplitTitle {
  readonly accent: string;
  readonly rest: string;
}

function splitTitle(title: string): SplitTitle {
  const trimmed = title.trim();
  const firstSpace = trimmed.indexOf(' ');
  if (firstSpace === -1) {
    return { accent: trimmed, rest: '' };
  }
  return {
    accent: trimmed.slice(0, firstSpace),
    rest: trimmed.slice(firstSpace),
  };
}

/**
 * DiscoverHeroStrip — top-of-discover hero band.
 *
 * Renders the page `<h1>` with an em-styled accent on the first word, a
 * compact facet meta line (total · neighborhoods · window). At ≤720 (WEB-680 /
 * DPL-29) the H1 is 27px and the meta line is CSS-hidden. WEB-1228 (R12):
 * the hero carries no search field — ⌘K is the one search entry.
 *
 * Pure presentational server component — data flows in via props. The
 * dusk gradient lives on `.discover-hero-strip` in `styles/components/discover-row.css` (WEB-688)
 * so light mode can override it from the delta stylesheet. Dark keeps the
 * LOCI Indigo Deep top stop fading into `--loci-color-background`.
 */
export function DiscoverHeroStrip({
  title,
  cityName,
  total,
  neighborhoods,
  window,
  viewerName,
  personalizedMeta,
  className,
}: DiscoverHeroStripProps): React.ReactElement {
  // FRE-917 — `viewerName` controls ONLY the heading grammar (`Discover, {name}.`
  // when a real viewer name is supplied; otherwise the DS 1.5.0 city grammar
  // `Discover, {cityName}.`, then the legacy free-form title path). The taste
  // meta line is gated separately on `isPersonalized` (the personalization
  // signal) so a personalized response with a blank/missing name still renders
  // the taste framing instead of dropping to the generic neighborhoods meta —
  // keeping the hero strip consistent with the coord strip / results bar.
  const trimmedViewerName = viewerName?.trim() ?? '';
  const usePersonalGrammar = trimmedViewerName.length > 0;
  const isPersonalized = personalizedMeta !== undefined;
  // DS 1.5.0 Discover grammar takes precedence when a city name is supplied:
  // `Discover, <em>{cityName}</em>.`. Otherwise fall back to the legacy
  // first-word-accent path on the free-form `title`.
  const useCityGrammar =
    !usePersonalGrammar && typeof cityName === 'string' && cityName.trim().length > 0;
  const { accent, rest } = splitTitle(title ?? '');
  // The accented lead is the viewer name (personalized) → city name (city
  // grammar) → first title word (legacy). The literal `Discover, ` lead and
  // trailing period wrap both name grammars identically.
  const useDiscoverGrammar = usePersonalGrammar || useCityGrammar;
  const grammarAccent = usePersonalGrammar ? trimmedViewerName : (cityName?.trim() ?? '');
  // Runtime guard: the union type requires at least one headline source, but a
  // blank string (`title=""` / `cityName="  "`) still satisfies it and would
  // render an empty `<h1>`. When neither path yields content, omit the heading
  // entirely (and drop the section's `aria-labelledby`, so it never points at an
  // absent id) rather than emit an empty, ARIA-labelling heading.
  const hasHeadline = useDiscoverGrammar || accent.length > 0;

  return (
    <section
      className={cn(
        // WEB-654 — `isolate` gives the section its own stacking context so the
        // `-z-10` art layer sits behind the copy while still painting above the
        // section's gradient. Safe here: nothing inside the hero renders an
        // escaping popover or portal.
        // WEB-1077 — padding and the H1/meta baseline row are
        // named classes in `styles/components/discover-hero.css` (DS 1.9.0
        // `.dv-top`). The ≤720 treatment (WEB-680) lives there too.
        'discover-hero-strip relative isolate w-full',
        className,
      )}
      aria-labelledby={hasHeadline ? 'discover-hero-strip-heading' : undefined}
      data-testid="discover-hero-strip"
    >
      <DiscoverHeroStripBackdrop />

      {/* WEB-1077 — DS 1.9.0 `.dv-top__row`: H1 and meta share one baseline;
          the meta wraps under the H1 only when the row runs out of room. */}
      <div className="discover-hero-strip__row" data-testid="discover-hero-strip-row">
        {hasHeadline && (
          <h1
            id="discover-hero-strip-heading"
            // WEB-1077 — `clamp(30px, 3vw, 42px)`, one line (`nowrap`), 27px at
            // ≤720 (WEB-680): `.discover-hero-strip__title`. CSS, not a JS
            // breakpoint, so SSR and hydration agree.
            className="discover-hero-strip__title text-loci-text font-display"
            data-testid="discover-hero-strip-title"
          >
            {useDiscoverGrammar ? (
              // DS 1.5.0 grammar: literal `Discover, ` lead + accented name + `.`.
              // The accented lead is the viewer name (FRE-917 personalized) or the
              // city name (generic). The lead string keeps the trailing space after
              // the comma; the period follows the <em> with no space, so the
              // rendered textContent is exactly `Discover, {name}.`.
              <>
                {'Discover, '}
                <em
                  className="not-italic text-ds-accent"
                  data-testid="discover-hero-strip-title-accent"
                >
                  {grammarAccent}
                </em>
                {'.'}
              </>
            ) : (
              <>
                <em
                  className="not-italic text-ds-accent"
                  data-testid="discover-hero-strip-title-accent"
                >
                  {accent}
                </em>
                {rest}
              </>
            )}
          </h1>
        )}

        <p
          // WEB-1077 — DS 1.9.0 `.dv-meta`: right-aligned beside the H1
          // (`flex: 1 1 360px`), wrapping under it when it does not fit.
          // WEB-680 / DPL-29 — hidden at ≤720 (counts live in the results
          // bar). CSS `hidden`, not an unmount, so SSR and the
          // first client paint agree.
          className="discover-hero-strip__meta max-[720px]:hidden font-mono text-caption leading-tight text-loci-text-muted md:text-small"
          data-testid="discover-hero-strip-meta"
        >
          <b className="font-semibold text-loci-text">
            {/*
             * WEB-786 — pending totals skeleton rather than a painted 0.
             * FRE-919 — once resolved, CountUpNumber seeds the real value on
             * first paint (SSR-safe) and ramps from ~80% on scroll-in.
             * WEB-1190 D5 — the count word is omitted until the number exists.
             */}
            {total == null ? (
              <DiscoverCountSkeleton
                data-testid="discover-hero-strip-total-skeleton"
                label="Loading event count"
              />
            ) : (
              <CountUpNumber value={total} data-testid="discover-hero-strip-total" />
            )}
          </b>
          {isPersonalized ? (
            // FRE-917 — taste meta line. Gated on the personalization signal
            // (`personalizedMeta` present), NOT on `viewerName`, so a personalized
            // response with a blank/missing name still shows the taste framing.
            // `matches` is the label for `total` and is omitted while that count
            // is unknown (WEB-1190 D5). Follow fragments keep their own numbers.
            <span data-testid="discover-hero-strip-meta-taste">
              {total == null ? '' : ' matches'}
              {personalizedMeta && personalizedMeta.followedArtistCount > 0
                ? ` · ${formatCount(personalizedMeta.followedArtistCount)} from artists you follow`
                : ''}
              {personalizedMeta && personalizedMeta.followedVenueCount > 0
                ? ` · ${formatCount(personalizedMeta.followedVenueCount)} at venues you follow`
                : ''}
            </span>
          ) : (
            <>
              {total == null ? '' : ' events'}
              {/* FRE-1239 — pluralize neighborhood(s) so a single-neighborhood
                facet doesn't read "1 neighborhoods". WEB-786 — skeleton the
                neighborhood count until the shared envelope resolves.
                WEB-1190 D5 — never paint neighborhoods without the number. */}
              {neighborhoods == null ? (
                <>
                  {' · '}
                  <DiscoverCountSkeleton
                    data-testid="discover-hero-strip-neighborhoods-skeleton"
                    label={null}
                  />
                </>
              ) : (
                <>
                  {' · '}
                  {formatCount(neighborhoods)}{' '}
                  {neighborhoods === 1 ? 'neighborhood' : 'neighborhoods'}
                </>
              )}
              {' · '}
              {window}
            </>
          )}
        </p>
      </div>
    </section>
  );
}
